import React, { useState, useRef } from 'react';
import './App.css';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import AdminPanel from './AdminPanel';
import supabase from './supabaseClient';
import { UDYAM_REG_NUMBER } from './constants';

function generateCertificateId() {
  // Simple unique ID: GSX-INT- + 6 random digits
  return 'GSX-INT-' + Math.floor(100000 + Math.random() * 900000);
}

function CertificateApp() {
  const [form, setForm] = useState({
    name: '',
    duration: '',
    department: '',
    startDate: '',
    endDate: '',
    issueDate: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [certificateId, setCertificateId] = useState('');
  const certRef = useRef();
  const [showModal, setShowModal] = useState(false);
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifyInput, setVerifyInput] = useState('');
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [adminAuth, setAdminAuth] = useState(false);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [adminLoginForm, setAdminLoginForm] = useState({ username: '', password: '' });
  const [adminLoginError, setAdminLoginError] = useState('');
  const [adminAuthLoading, setAdminAuthLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newCertificateId = generateCertificateId();
    setCertificateId(newCertificateId);
    setSubmitted(true);
    // Save to Supabase
    const { error } = await supabase.from('certificates').insert([
      {
        name: form.name,
        duration: form.duration,
        department: form.department,
        startDate: form.startDate,
        endDate: form.endDate,
        issueDate: form.issueDate,
        certificateId: newCertificateId,
      }
    ]);
    if (error) {
      setSubmitted(false);
      setShowThankYouModal(false);
      alert('Error saving certificate: ' + error.message);
    } else {
      setShowThankYouModal(true);
    }
  };

  const handleDownload = async () => {
    const input = certRef.current;
    const canvas = await html2canvas(input, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: [canvas.width, canvas.height] });
    pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
    pdf.save(`certificate_${form.name}.pdf`);
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setVerifyLoading(true);
    setVerifyResult(null);
    const { data, error } = await supabase
      .from('certificates')
      .select('*')
      .eq('certificateId', verifyInput)
      .single();
    setVerifyLoading(false);
    if (error || !data) setVerifyResult('Certificate not found.');
    else setVerifyResult(data);
  };

  // Admin login handler
  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setAdminLoginError('');
    setAdminAuthLoading(true);
    // Query the users table for the username
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('username', adminLoginForm.username)
      .single();
    if (error || !data) {
      setAdminLoginError('Invalid credentials');
      setAdminAuthLoading(false);
      return;
    }
    // WARNING: For demo only! In production, hash the password and compare hashes.
    if (adminLoginForm.password === data.password_hash && data.is_admin) {
      setAdminAuth(true);
      setShowAdminLogin(false);
      setShowModal(true);
    } else {
      setAdminLoginError('Invalid credentials');
    }
    setAdminAuthLoading(false);
  };

  return (
    <div className="App" style={{ minHeight: '100vh', background: '#f4f6fa', padding: '2rem' }}>
      <h1 style={{ color: '#162935', marginBottom: 0 }}>Global ServiceX Certificate System</h1>
      <p style={{ color: '#2c3f48', marginTop: 0 }}>Choose an option below:</p>
      <div style={{ display: 'flex', gap: 24, justifyContent: 'center', margin: '2rem 0' }}>
        <button onClick={() => {
          if (!adminAuth) setShowAdminLogin(true);
          else setShowModal(true);
        }} style={{ background: 'linear-gradient(90deg,#162935,#2c3f48)', color: '#fff', padding: '12px 32px', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 18, boxShadow: '0 2px 8px #0002' }}>Get Instant Certificate</button>
        <button onClick={() => setShowVerifyModal(true)} style={{ background: '#FFC727', color: '#162935', padding: '12px 32px', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 18, boxShadow: '0 2px 8px #0002' }}>Verify Your Certificate</button>
      </div>
      {/* Admin Login Modal */}
      {showAdminLogin && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(34,34,34,0.18)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 20, boxShadow: '0 8px 32px #0002', padding: 36, maxWidth: 400, width: '100%', position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <button onClick={() => setShowAdminLogin(false)} style={{ position: 'absolute', top: 12, right: 18, background: 'none', border: 'none', fontSize: 28, color: '#162935', cursor: 'pointer', zIndex: 10 }} title="Close">&times;</button>
            <img src="/logo.png" alt="Logo" style={{ width: 70, height: 70, borderRadius: '50%', marginBottom: 12, objectFit: 'cover', background: '#fff', border: '2px solid #2c3f48' }} />
            <h2 style={{ color: '#162935', marginBottom: 8, textAlign: 'center', fontWeight: 900, fontSize: 30, letterSpacing: 1 }}>Admin Login</h2>
            <div style={{ color: '#64748b', fontSize: 16, marginBottom: 18, textAlign: 'center' }}>Admin access required to generate certificates.</div>
            <form onSubmit={handleAdminLogin} style={{ display: 'flex', flexDirection: 'column', gap: 18, width: '100%' }}>
              <input type="text" placeholder="Username" value={adminLoginForm.username} onChange={e => setAdminLoginForm({ ...adminLoginForm, username: e.target.value })} style={{ padding: 14, borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 16, background: '#f8fafc', outline: 'none', transition: 'border 0.2s' }} required />
              <input type="password" placeholder="Password" value={adminLoginForm.password} onChange={e => setAdminLoginForm({ ...adminLoginForm, password: e.target.value })} style={{ padding: 14, borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 16, background: '#f8fafc', outline: 'none', transition: 'border 0.2s' }} required />
              <button type="submit" style={{ padding: '14px 0', borderRadius: 10, background: 'linear-gradient(90deg,#162935,#2c3f48)', color: '#fff', border: 'none', fontWeight: 800, fontSize: 18, cursor: 'pointer', marginTop: 8, boxShadow: '0 2px 8px #0001', letterSpacing: 1 }} disabled={adminAuthLoading}>{adminAuthLoading ? 'Logging in...' : 'Login'}</button>
              {adminLoginError && <div style={{ color: 'red', marginTop: 8, textAlign: 'center' }}>{adminLoginError}</div>}
            </form>
            <div style={{ color: '#888', fontSize: 13, marginTop: 18, textAlign: 'center' }}>
              <b>Note:</b> For demo only. In production, always hash passwords and use HTTPS.
            </div>
          </div>
        </div>
      )}
      {/* Modal Popup for Certificate Form/Certificate */}
      {showModal && adminAuth && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(34,34,34,0.18)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 8px 32px 0 rgba(34,34,34,0.18)', padding: 0, position: 'relative', maxWidth: 1000, width: '95vw', maxHeight: '95vh', overflowY: 'auto' }}>
            <button onClick={() => setShowModal(false)} style={{ position: 'absolute', top: 12, right: 18, background: 'none', border: 'none', fontSize: 28, color: '#162935', cursor: 'pointer', zIndex: 10 }} title="Close">&times;</button>
            {/* Certificate Form and Certificate Rendering */}
            <div style={{ padding: 0 }}>
              <form
                onSubmit={handleSubmit}
                style={{
                  margin: '2rem auto',
                  maxWidth: 500,
                  background: '#fff',
                  padding: 28,
                  borderRadius: 12,
                  boxShadow: '0 2px 12px #0001',
                }}
              >
                <div style={{ marginBottom: 16 }}>
                  <label>Full Name of Intern:</label>
                  <input type="text" name="name" value={form.name} onChange={handleChange} required placeholder="Full Name" style={{ width: '100%', padding: 8, marginTop: 4 }} />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label>Duration (e.g., 2 months):</label>
                  <input type="text" name="duration" value={form.duration} onChange={handleChange} required placeholder="Duration" style={{ width: '100%', padding: 8, marginTop: 4 }} />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label>Department/Role:</label>
                  <input type="text" name="department" value={form.department} onChange={handleChange} required placeholder="e.g., Digital Marketing" style={{ width: '100%', padding: 8, marginTop: 4 }} />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label>Start Date:</label>
                  <input type="date" name="startDate" value={form.startDate} onChange={handleChange} required style={{ width: '100%', padding: 8, marginTop: 4 }} />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label>End Date:</label>
                  <input type="date" name="endDate" value={form.endDate} onChange={handleChange} required style={{ width: '100%', padding: 8, marginTop: 4 }} />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label>Date of Issue:</label>
                  <input type="date" name="issueDate" value={form.issueDate} onChange={handleChange} required style={{ width: '100%', padding: 8, marginTop: 4 }} />
                </div>
                <button type="submit" style={{ background: 'linear-gradient(90deg,#162935,#2c3f48)', color: '#fff', padding: '10px 24px', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600, fontSize: 16, boxShadow: '0 2px 8px #0002' }}>Generate Certificate</button>
              </form>
              {/* Certificate Preview and Thank You Modal */}
              {showThankYouModal && (
                <div style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: '100vw',
                  height: '100vh',
                  background: 'rgba(34,34,34,0.18)',
                  zIndex: 2000,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <div style={{
                    background: '#fff',
                    borderRadius: 24,
                    boxShadow: '0 8px 32px 0 rgba(34,34,34,0.18)',
                    padding: 0,
                    position: 'relative',
                    maxWidth: 1000,
                    width: '95vw',
                    maxHeight: '98vh',
                    overflowY: 'auto',
                  }}>
                    <button onClick={() => {
                      setShowThankYouModal(false);
                      setShowModal(false);
                      setSubmitted(false);
                      setForm({ name: '', duration: '', department: '', startDate: '', endDate: '', issueDate: '' });
                    }} style={{ position: 'absolute', top: 12, right: 18, background: 'none', border: 'none', fontSize: 28, color: '#162935', cursor: 'pointer', zIndex: 10 }} title="Close">&times;</button>
                    <div style={{ padding: 32, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div style={{ fontSize: 36, color: '#162935', fontWeight: 900, marginBottom: 12 }}>Thank You!</div>
                      <div style={{ fontSize: 18, color: '#2c3f48', marginBottom: 24 }}>Your certificate has been generated successfully.</div>
                      {/* Certificate Preview (same as below) */}
                      <div
                        ref={certRef}
                        style={{
                          position: 'relative',
                          width: 900,
                          height: 640,
                          margin: '0 auto',
                          background: '#fff',
                          border: '6px solid #162935',
                          borderRadius: 32,
                          boxShadow: '0 8px 32px 0 rgba(34,34,34,0.18)',
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                        }}
                      >
                        {/* Main content area - adjust padding for new border spacing */}
                        <div style={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                          right: 8,
                          bottom: 8,
                          borderRadius: 4,
                          zIndex: 4,
                          padding: '24px 24px 90px 24px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          background: 'none',
                        }}>
                          {/* Modern Triangles/Geometric Corners with website palette */}
                          {/* Top Left Corner - Layered triangles and yellow accent line */}
                          <svg style={{position:'absolute',top:-20,left:-20,zIndex:2}} width="120" height="120" viewBox="0 0 90 90">
                            <polygon points="0,0 90,0 0,90" fill="#FFC727" opacity="0.18"/>
                            <polygon points="0,0 70,0 0,70" fill="#162935" opacity="0.10"/>
                            <polyline points="0,0 70,70 0,90" fill="none" stroke="#FFE9A0" strokeWidth="3" opacity="0.7"/>
                          </svg>
                          {/* Top Right Corner - same triangle, mirrored horizontally */}
                          <svg style={{position:'absolute',top:-20,right:-20,zIndex:2,transform:'scaleX(-1)'}} width="150" height="150" viewBox="0 0 90 90">
                            <polygon points="0,0 90,0 0,90" fill="#FFC727" opacity="0.18"/>
                            <polygon points="0,0 70,0 0,70" fill="#162935" opacity="0.10"/>
                            <polyline points="0,0 70,70 0,90" fill="none" stroke="#FFE9A0" strokeWidth="3" opacity="0.7"/>
                          </svg>
                          {/* Bottom Left Corner - same triangle, mirrored vertically */}
                          <svg style={{position:'absolute',bottom:-20,left:-20,zIndex:2,transform:'scaleY(-1)'}} width="150" height="150" viewBox="0 0 90 90">
                            <polygon points="0,0 90,0 0,90" fill="#FFC727" opacity="0.18"/>
                            <polygon points="0,0 70,0 0,70" fill="#162935" opacity="0.10"/>
                            <polyline points="0,0 70,70 0,90" fill="none" stroke="#FFE9A0" strokeWidth="3" opacity="0.7"/>
                          </svg>
                          {/* Bottom Right Corner - same triangle, mirrored both axes */}
                          <svg style={{position:'absolute',bottom:-20,right:-20,zIndex:2,transform:'scale(-1,-1)'}} width="120" height="120" viewBox="0 0 90 90">
                            <polygon points="0,0 90,0 0,90" fill="#FFC727" opacity="0.18"/>
                            <polygon points="0,0 70,0 0,70" fill="#162935" opacity="0.10"/>
                            <polyline points="0,0 70,70 0,90" fill="none" stroke="#FFE9A0" strokeWidth="3" opacity="0.7"/>
                          </svg>
                          {/* Watermark */}
                          <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%) rotate(-20deg)',
                            fontSize: 81.5,
                            color: '#FFC727',
                            opacity: 0.18,
                            fontWeight: 900,
                            pointerEvents: 'none',
                            zIndex: 0,
                            userSelect: 'none',
                            whiteSpace: 'nowrap',
                            letterSpacing: 4,
                            fontFamily: 'Montserrat, Arial, sans-serif',
                            textShadow: '0 2px 8px #fff',
                            width: '100%',
                            textAlign: 'center',
                            lineHeight: 1.1
                          }}>
                            GLOBAL SERVICEX
                          </div>
                          {/* Badge using uploaded badge.png or uploaded badge */}
                          <div style={{ position: 'absolute', top: 40, right: 60, zIndex: 2, width: 120, height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img src="/badge.png" alt="Badge" style={{ width: 120, height: 120, objectFit: 'contain' }} onError={e => { e.target.onerror = null; e.target.src = '/badge.jpg'; }} />
                          </div>
                          {/* Logo and tagline */}
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 8, zIndex: 2, position: 'relative', marginTop: -12 }}>
                            <img src="/logo.png" alt="Logo" style={{ width: 90, height: 90, borderRadius: '50%', marginBottom: 8, objectFit: 'cover', background: '#fff', border: '2px solid #2c3f48' }} />
                            <div style={{ fontSize: 32, color: '#162935', fontWeight: 900, letterSpacing: 2 }}>GLOBAL SERVICEX</div>
                            <div style={{ fontSize: 15, color: '#FFC727', marginBottom: 8, fontWeight: 700 }}>Technology | Design | Growth</div>
                          </div>
                          <div style={{ fontSize: 26, color: '#162935', fontWeight: 600, marginBottom: 8, letterSpacing: 1, zIndex: 2, position: 'relative' }}>Certificate of Completion</div>
                          <div style={{ fontSize: 16, color: '#2c3f48', marginBottom: 20, zIndex: 2, position: 'relative' }}>This is to certify that</div>
                          <div style={{ fontSize: 24, color: 'red', fontWeight: 700, marginBottom: 14, letterSpacing: 1, zIndex: 2, position: 'relative', textTransform: 'uppercase' }}>{form.name}</div>
                          <div style={{ fontSize: 16, color: '#2c3f48', marginBottom: 8, zIndex: 2, position: 'relative' }}>has successfully completed a</div>
                          <div style={{ fontSize: 20, color: '#162935', fontWeight: 700, marginBottom: 8, zIndex: 2, position: 'relative' }}>
                            <b>{(() => {
                              const n = Number(form.duration);
                              if (!isNaN(n) && n > 0) {
                                return n + ' month' + (n === 1 ? '' : 's');
                              }
                              return form.duration;
                            })()}</b> <b>Internship Program</b>
                          </div>
                          <div style={{ fontSize: 16, color: '#2c3f48', marginBottom: 8, zIndex: 2, position: 'relative' }}>at Global ServiceX in the domain of</div>
                          <div style={{ fontSize: 20, color: '#162935', fontWeight: 700, marginBottom: 14, zIndex: 2, position: 'relative' }}><b>{form.department}</b></div>
                          <div style={{ fontSize: 15, color: '#2c3f48', marginBottom: 14, zIndex: 2, position: 'relative' }}>
                            During the internship period from <b>{form.startDate}</b> to <b>{form.endDate}</b>,<br />
                            the intern has demonstrated consistent dedication, strong work ethic,<br />
                            and valuable contributions to ongoing projects and team activities.
                          </div>
                          <div style={{ fontSize: 15, color: '#2c3f48', marginBottom: 14, zIndex: 2, position: 'relative' }}>
                            We truly appreciate their efforts and wish them the best for future endeavors.
                          </div>
                          {/* Bottom Info and Signature Blocks in Corners */}
                          <div style={{
                            position: 'absolute',
                            left: 6,
                            bottom: -10,
                            padding: 0,
                            textAlign: 'left',
                            fontSize: 13,
                            color: '#90848e',
                            zIndex: 3,
                            lineHeight: 1.7,
                            maxWidth: 340
                          }}>
                            <div>📍Issued at: Global ServiceX, Dehradun, Uttarakhand</div>
                            <div>📅 Date of Issue: {form.issueDate}</div>
                            <div>🔒 Certificate ID: {certificateId}</div>
                            <div>🏢 Udyam Registration Number: {UDYAM_REG_NUMBER}</div>
                          </div>
                          <div style={{
                            position: 'absolute',
                            right: 6,
                            bottom: 0,
                            padding: 0,
                            textAlign: 'right',
                            fontSize: 15,
                            color: '#162935',
                            fontWeight: 600,
                            zIndex: 3,
                            minWidth: 180
                          }}>
                            <div>
                              <img src="/signature.png" alt="Signature" style={{ width: 110, height: 'auto', marginBottom: -18.5, marginTop: -10 }} />
                            </div>
                            <div style={{ fontSize: 15, color: '#162935', fontWeight: 600, marginTop: 2, marginRight: 20 }}>Alok Mishra</div>
                            <div style={{ fontSize: 13, color: '#90848e', fontWeight: 400, marginRight: 15 }}>Founder & CEO</div>
                          </div>
                        </div>
                      </div>
                      <button onClick={handleDownload} style={{ display: 'block', margin: '32px auto 0 auto', background: 'linear-gradient(90deg,#162935,#2c3f48)', color: '#fff', padding: '12px 32px', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: 18, boxShadow: '0 2px 8px #0002' }}>Download as PDF</button>
                    </div>
                  </div>
                </div>
              )}
              {submitted && !showThankYouModal && (
                <>
                  <div
                    ref={certRef}
                    style={{
                      position: 'relative',
                      width: 900,
                      height: 640,
                      margin: '0 auto',
                      background: '#fff', // solid white background
                      border: '6px solid #162935', // dark blue border
                      borderRadius: 32,
                      boxShadow: '0 8px 32px 0 rgba(34,34,34,0.18)',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                    }}
                  >
                    {/* Main content area - adjust padding for new border spacing */}
                    <div style={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      right: 8,
                      bottom: 8,
                      borderRadius: 4,
                      zIndex: 4,
                      padding: '24px 24px 90px 24px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      background: 'none',
                    }}>
                      {/* Modern Triangles/Geometric Corners with website palette */}
                      {/* Top Left Corner - Layered triangles and yellow accent line */}
                      <svg style={{position:'absolute',top:-20,left:-20,zIndex:2}} width="120" height="120" viewBox="0 0 90 90">
                        <polygon points="0,0 90,0 0,90" fill="#FFC727" opacity="0.18"/>
                        <polygon points="0,0 70,0 0,70" fill="#162935" opacity="0.10"/>
                        <polyline points="0,0 70,70 0,90" fill="none" stroke="#FFE9A0" strokeWidth="3" opacity="0.7"/>
                      </svg>
                      {/* Top Right Corner - same triangle, mirrored horizontally */}
                      <svg style={{position:'absolute',top:-20,right:-20,zIndex:2,transform:'scaleX(-1)'}} width="150" height="150" viewBox="0 0 90 90">
                        <polygon points="0,0 90,0 0,90" fill="#FFC727" opacity="0.18"/>
                        <polygon points="0,0 70,0 0,70" fill="#162935" opacity="0.10"/>
                        <polyline points="0,0 70,70 0,90" fill="none" stroke="#FFE9A0" strokeWidth="3" opacity="0.7"/>
                      </svg>
                      {/* Bottom Left Corner - same triangle, mirrored vertically */}
                      <svg style={{position:'absolute',bottom:-20,left:-20,zIndex:2,transform:'scaleY(-1)'}} width="150" height="150" viewBox="0 0 90 90">
                        <polygon points="0,0 90,0 0,90" fill="#FFC727" opacity="0.18"/>
                        <polygon points="0,0 70,0 0,70" fill="#162935" opacity="0.10"/>
                        <polyline points="0,0 70,70 0,90" fill="none" stroke="#FFE9A0" strokeWidth="3" opacity="0.7"/>
                      </svg>
                      {/* Bottom Right Corner - same triangle, mirrored both axes */}
                      <svg style={{position:'absolute',bottom:-20,right:-20,zIndex:2,transform:'scale(-1,-1)'}} width="120" height="120" viewBox="0 0 90 90">
                        <polygon points="0,0 90,0 0,90" fill="#FFC727" opacity="0.18"/>
                        <polygon points="0,0 70,0 0,70" fill="#162935" opacity="0.10"/>
                        <polyline points="0,0 70,70 0,90" fill="none" stroke="#FFE9A0" strokeWidth="3" opacity="0.7"/>
                      </svg>
                      {/* Watermark */}
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%) rotate(-20deg)',
                        fontSize: 81.5,
                        color: '#FFC727',
                        opacity: 0.18,
                        fontWeight: 900,
                        pointerEvents: 'none',
                        zIndex: 0,
                        userSelect: 'none',
                        whiteSpace: 'nowrap',
                        letterSpacing: 4,
                        fontFamily: 'Montserrat, Arial, sans-serif',
                        textShadow: '0 2px 8px #fff',
                        width: '100%',
                        textAlign: 'center',
                        lineHeight: 1.1
                      }}>
                        GLOBAL SERVICEX
                      </div>
                      {/* Badge using uploaded badge.png or uploaded badge */}
                      <div style={{ position: 'absolute', top: 40, right: 60, zIndex: 2, width: 120, height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img src="/badge.png" alt="Badge" style={{ width: 120, height: 120, objectFit: 'contain' }} onError={e => { e.target.onerror = null; e.target.src = '/badge.jpg'; }} />
                      </div>
                      {/* Logo and tagline */}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 8, zIndex: 2, position: 'relative', marginTop: -12 }}>
                        <img src="/logo.png" alt="Logo" style={{ width: 90, height: 90, borderRadius: '50%', marginBottom: 8, objectFit: 'cover', background: '#fff', border: '2px solid #2c3f48' }} />
                        <div style={{ fontSize: 32, color: '#162935', fontWeight: 900, letterSpacing: 2 }}>GLOBAL SERVICEX</div>
                        <div style={{ fontSize: 15, color: '#FFC727', marginBottom: 8, fontWeight: 700 }}>Technology | Design | Growth</div>
                      </div>
                      <div style={{ fontSize: 26, color: '#162935', fontWeight: 600, marginBottom: 8, letterSpacing: 1, zIndex: 2, position: 'relative' }}>Certificate of Completion</div>
                      <div style={{ fontSize: 16, color: '#2c3f48', marginBottom: 20, zIndex: 2, position: 'relative' }}>This is to certify that</div>
                      <div style={{ fontSize: 24, color: 'red', fontWeight: 700, marginBottom: 14, letterSpacing: 1, zIndex: 2, position: 'relative', textTransform: 'uppercase' }}>{form.name}</div>
                      <div style={{ fontSize: 16, color: '#2c3f48', marginBottom: 8, zIndex: 2, position: 'relative' }}>has successfully completed a</div>
                      <div style={{ fontSize: 20, color: '#162935', fontWeight: 700, marginBottom: 8, zIndex: 2, position: 'relative' }}>
                        <b>{(() => {
                          const n = Number(form.duration);
                          if (!isNaN(n) && n > 0) {
                            return n + ' month' + (n === 1 ? '' : 's');
                          }
                          return form.duration;
                        })()}</b> <b>Internship Program</b>
                      </div>
                      <div style={{ fontSize: 16, color: '#2c3f48', marginBottom: 8, zIndex: 2, position: 'relative' }}>at Global ServiceX in the domain of</div>
                      <div style={{ fontSize: 20, color: '#162935', fontWeight: 700, marginBottom: 14, zIndex: 2, position: 'relative' }}><b>{form.department}</b></div>
                      <div style={{ fontSize: 15, color: '#2c3f48', marginBottom: 14, zIndex: 2, position: 'relative' }}>
                        During the internship period from <b>{form.startDate}</b> to <b>{form.endDate}</b>,<br />
                        the intern has demonstrated consistent dedication, strong work ethic,<br />
                        and valuable contributions to ongoing projects and team activities.
                      </div>
                      <div style={{ fontSize: 15, color: '#2c3f48', marginBottom: 14, zIndex: 2, position: 'relative' }}>
                        We truly appreciate their efforts and wish them the best for future endeavors.
                      </div>
                      {/* Bottom Info and Signature Blocks in Corners */}
                      <div style={{
                        position: 'absolute',
                        left: 6,
                        bottom: -10,
                        padding: 0,
                        textAlign: 'left',
                        fontSize: 13,
                        color: '#90848e',
                        zIndex: 3,
                        lineHeight: 1.7,
                        maxWidth: 340
                      }}>
                        <div>📍Issued at: Global ServiceX, Dehradun, Uttarakhand</div>
                        <div>📅 Date of Issue: {form.issueDate}</div>
                        <div>🔒 Certificate ID: {certificateId}</div>
                        <div>🏢 Udyam Registration Number: {UDYAM_REG_NUMBER}</div>
                      </div>
                      <div style={{
                        position: 'absolute',
                        right: 6,
                        bottom: 0,
                        padding: 0,
                        textAlign: 'right',
                        fontSize: 15,
                        color: '#162935',
                        fontWeight: 600,
                        zIndex: 3,
                        minWidth: 180
                      }}>
                        <div>
                          <img src="/signature.png" alt="Signature" style={{ width: 110, height: 'auto', marginBottom: -18.5, marginTop: -10 }} />
                        </div>
                        <div style={{ fontSize: 15, color: '#162935', fontWeight: 600, marginTop: 2, marginRight: 20 }}>Alok Mishra</div>
                        <div style={{ fontSize: 13, color: '#90848e', fontWeight: 400, marginRight: 15 }}>Founder & CEO</div>
                      </div>
                    </div>
                  </div>
                  <button onClick={handleDownload} style={{ display: 'block', margin: '24px auto', background: 'linear-gradient(90deg,#162935,#2c3f48)', color: '#fff', padding: '10px 24px', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600, fontSize: 16, boxShadow: '0 2px 8px #0002' }}>Download as PDF</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {showVerifyModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(34,34,34,0.18)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 8px 32px 0 rgba(34,34,34,0.18)', padding: 0, position: 'relative', maxWidth: 500, width: '95vw', maxHeight: '95vh', overflowY: 'auto' }}>
            <button onClick={() => { setShowVerifyModal(false); setVerifyInput(''); setVerifyResult(null); }} style={{ position: 'absolute', top: 12, right: 18, background: 'none', border: 'none', fontSize: 28, color: '#162935', cursor: 'pointer', zIndex: 10 }} title="Close">&times;</button>
            <div style={{ padding: 32 }}>
              <h2 style={{ color: '#FFC727', marginBottom: 24 }}>Verify Certificate</h2>
              <form onSubmit={handleVerify} style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 24 }}>
                <input type="text" placeholder="Enter Certificate ID" value={verifyInput} onChange={e => setVerifyInput(e.target.value)} style={{ padding: 10, width: 240, borderRadius: 6, border: '1px solid #ccc' }} required />
                <button type="submit" style={{ padding: '10px 24px', borderRadius: 6, background: 'linear-gradient(90deg,#FFC727,#FFD95A)', color: '#162935', border: 'none', fontWeight: 600, fontSize: 16, cursor: 'pointer' }}>Verify</button>
              </form>
              {verifyLoading && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '24px 0' }}>
                  <div style={{ border: '4px solid #f3f3f3', borderTop: '4px solid #FFC727', borderRadius: '50%', width: 40, height: 40, animation: 'spin 1s linear infinite' }} />
                  <style>{'@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }'}</style>
                </div>
              )}
              {verifyResult && typeof verifyResult === 'string' && (
                <div style={{ color: 'red', fontWeight: 600, marginTop: 16 }}>{verifyResult}</div>
              )}
              {verifyResult && typeof verifyResult === 'object' && (
                <>
                  <div style={{ marginTop: 16, background: '#f4f6fa', borderRadius: 10, padding: 24, boxShadow: '0 2px 8px #0001' }}>
                    <h3 style={{ color: '#162935', marginBottom: 12 }}>Certificate Details</h3>
                    <div><b>Name:</b> {verifyResult.name}</div>
                    <div><b>Certificate ID:</b> {verifyResult.certificateId}</div>
                    <div><b>Department:</b> {verifyResult.department}</div>
                    <div><b>Duration:</b> {verifyResult.duration}</div>
                    <div><b>Start Date:</b> {verifyResult.startDate}</div>
                    <div><b>End Date:</b> {verifyResult.endDate}</div>
                    <div><b>Issue Date:</b> {verifyResult.issueDate}</div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
                    <button onClick={() => { setPreviewData(verifyResult); setShowPreviewModal(true); }} style={{ padding: '12px 32px', borderRadius: 6, background: 'linear-gradient(90deg,#162935,#2c3f48)', color: '#fff', border: 'none', fontWeight: 600, fontSize: 18, cursor: 'pointer', boxShadow: '0 2px 8px #0002' }}>Preview Certificate</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {showPreviewModal && previewData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(34,34,34,0.18)',
          zIndex: 4000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 24,
            boxShadow: '0 8px 32px 0 rgba(34,34,34,0.18)',
            padding: 0,
            position: 'relative',
            maxWidth: 1000,
            width: '95vw',
            maxHeight: '98vh',
            overflowY: 'auto',
          }}>
            <button onClick={() => setShowPreviewModal(false)} style={{ position: 'absolute', top: 12, right: 18, background: 'none', border: 'none', fontSize: 28, color: '#162935', cursor: 'pointer', zIndex: 10 }} title="Close">&times;</button>
            <div style={{ padding: 32, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {/* Main form certificate design (copy from AdminPanel.js) */}
              <div
                style={{
                  position: 'relative',
                  width: 900,
                  height: 640,
                  margin: '0 auto',
                  background: '#fff',
                  border: '6px solid #162935',
                  borderRadius: 32,
                  boxShadow: '0 8px 32px 0 rgba(34,34,34,0.18)',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                }}
              >
                {/* Corners and Watermark */}
                <svg style={{position:'absolute',top:-20,left:-20,zIndex:2}} width="120" height="120" viewBox="0 0 90 90">
                  <polygon points="0,0 90,0 0,90" fill="#FFC727" opacity="0.18"/>
                  <polygon points="0,0 70,0 0,70" fill="#162935" opacity="0.10"/>
                  <polyline points="0,0 70,70 0,90" fill="none" stroke="#FFE9A0" strokeWidth="3" opacity="0.7"/>
                </svg>
                <svg style={{position:'absolute',top:-20,right:-20,zIndex:2,transform:'scaleX(-1)'}} width="150" height="150" viewBox="0 0 90 90">
                  <polygon points="0,0 90,0 0,90" fill="#FFC727" opacity="0.18"/>
                  <polygon points="0,0 70,0 0,70" fill="#162935" opacity="0.10"/>
                  <polyline points="0,0 70,70 0,90" fill="none" stroke="#FFE9A0" strokeWidth="3" opacity="0.7"/>
                </svg>
                <svg style={{position:'absolute',bottom:-20,left:-20,zIndex:2,transform:'scaleY(-1)'}} width="150" height="150" viewBox="0 0 90 90">
                  <polygon points="0,0 90,0 0,90" fill="#FFC727" opacity="0.18"/>
                  <polygon points="0,0 70,0 0,70" fill="#162935" opacity="0.10"/>
                  <polyline points="0,0 70,70 0,90" fill="none" stroke="#FFE9A0" strokeWidth="3" opacity="0.7"/>
                </svg>
                <svg style={{position:'absolute',bottom:-20,right:-20,zIndex:2,transform:'scale(-1,-1)'}} width="120" height="120" viewBox="0 0 90 90">
                  <polygon points="0,0 90,0 0,90" fill="#FFC727" opacity="0.18"/>
                  <polygon points="0,0 70,0 0,70" fill="#162935" opacity="0.10"/>
                  <polyline points="0,0 70,70 0,90" fill="none" stroke="#FFE9A0" strokeWidth="3" opacity="0.7"/>
                </svg>
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%) rotate(-20deg)',
                  fontSize: 81.5,
                  color: '#FFC727',
                  opacity: 0.18,
                  fontWeight: 900,
                  pointerEvents: 'none',
                  zIndex: 0,
                  userSelect: 'none',
                  whiteSpace: 'nowrap',
                  letterSpacing: 4,
                  fontFamily: 'Montserrat, Arial, sans-serif',
                  textShadow: '0 2px 8px #fff',
                  width: '100%',
                  textAlign: 'center',
                  lineHeight: 1.1
                }}>
                  GLOBAL SERVICEX
                </div>
                {/* Badge */}
                <div style={{ position: 'absolute', top: 40, right: 60, zIndex: 2, width: 120, height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <img src="/badge.png" alt="Badge" style={{ width: 120, height: 120, objectFit: 'contain' }} onError={e => { e.target.onerror = null; e.target.src = '/badge.jpg'; }} />
                </div>
                {/* Centered Content */}
                <div style={{
                  width: '100%',
                  maxWidth: 700,
                  margin: '0 auto',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 2,
                  position: 'relative',
                  marginBottom: 100,
                  marginTop: 24,
                }}>
                  {/* Logo and tagline */}
                  <img src="/logo.png" alt="Logo" style={{ width: 90, height: 90, borderRadius: '50%', marginBottom: 8, objectFit: 'cover', background: '#fff', border: '2px solid #2c3f48' }} />
                  <div style={{ fontSize: 32, color: '#162935', fontWeight: 900, letterSpacing: 2 }}>GLOBAL SERVICEX</div>
                  <div style={{ fontSize: 15, color: '#FFC727', marginBottom: 8, fontWeight: 700 }}>Technology | Design | Growth</div>
                  <div style={{ fontSize: 26, color: '#162935', fontWeight: 600, marginBottom: 8, letterSpacing: 1 }}>Certificate of Completion</div>
                  <div style={{ fontSize: 16, color: '#2c3f48', marginBottom: 20 }}>This is to certify that</div>
                  <div style={{ fontSize: 24, color: 'red', fontWeight: 700, marginBottom: 14, letterSpacing: 1, textTransform: 'uppercase' }}>{previewData.name}</div>
                  <div style={{ fontSize: 16, color: '#2c3f48', marginBottom: 8 }}>has successfully completed a</div>
                  <div style={{ fontSize: 20, color: '#162935', fontWeight: 700, marginBottom: 8 }}>
                    <b>{(() => {
                      const n = Number(previewData.duration);
                      if (!isNaN(n) && n > 0) {
                        return n + ' month' + (n === 1 ? '' : 's');
                      }
                      return previewData.duration;
                    })()}</b> <b>Internship Program</b>
                  </div>
                  <div style={{ fontSize: 16, color: '#2c3f48', marginBottom: 8 }}>at Global ServiceX in the domain of</div>
                  <div style={{ fontSize: 20, color: '#162935', fontWeight: 700, marginBottom: 14 }}><b>{previewData.department}</b></div>
                  <div style={{ fontSize: 15, color: '#2c3f48', marginBottom: 14 }}>
                    During the internship period from <b>{previewData.startDate}</b> to <b>{previewData.endDate}</b>,<br />
                    the intern has demonstrated consistent dedication, strong work ethic,<br />
                    and valuable contributions to ongoing projects and team activities.
                  </div>
                  <div style={{ fontSize: 15, color: '#2c3f48', marginBottom: 14 }}>
                    We truly appreciate their efforts and wish them the best for future endeavors.
                  </div>
                </div>
                {/* Bottom Info: Issued at, Date, Certificate ID (bottom left, flush to border) */}
                <div style={{ position: 'absolute', bottom: 4, left: 16, zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', fontSize: 15, color: '#888' }}>
                  <div style={{ marginBottom: 2 }}><span style={{ fontSize: 17 }}>📍</span> Issued at: Global ServiceX, Dehradun, Uttarakhand</div>
                  <div style={{ marginBottom: 2 }}><span style={{ fontSize: 17 }}>🗓️</span> Date of Issue: {previewData.issueDate}</div>
                  <div><span style={{ fontSize: 17 }}>🔒</span> Certificate ID: <span style={{ color: '#1976d2', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' }}>{previewData.certificateId}</span></div>
                  <div style={{ marginTop: 2 }}><span style={{ fontSize: 17 }}>🏢</span> Udyam Registration Number: {UDYAM_REG_NUMBER}</div>
                </div>
                {/* Bottom Right: Signature, Name, Title (flush to border) */}
                <div style={{ position: 'absolute', bottom: 16, right: 16, zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <img src="/signature.png" alt="Signature" style={{ width: 120, height: 60, objectFit: 'contain', marginBottom: 2 }} />
                  <div style={{ fontSize: 17, color: '#162935', fontWeight: 700 }}>Alok Mishra</div>
                  <div style={{ fontSize: 13, color: '#888' }}>Founder & CEO</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <style>{`
        @keyframes fadeInCert {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .fadeInCert {
          animation: fadeInCert 1s;
        }
      `}</style>
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<CertificateApp />} />
        <Route path="/cadmin" element={<AdminPanel />} />
      </Routes>
    </Router>
  );
}

export default App;
